import React from 'react';
import { format, isValid, parse } from 'date-fns';
import PropTypes from 'prop-types';

import {
  branchOfServiceRuleforCategories,
  CategoryBenefitsIssuesOutsidetheUS,
  CategoryEducation,
  CategoryGuardianshipCustodianshipFiduciaryIssues,
  CategoryHealthCare,
  CategoryHousingAssistanceAndHomeLoans,
  CategoryVeteranReadinessAndEmployment,
  CHAPTER_3,
  isQuestionAboutVeteranOrSomeoneElseLabels,
  relationshipOptionsMyself,
  relationshipOptionsSomeoneElse,
  statesRequiringPostalCode,
  TopicAppraisals,
  TopicDisabilityCompensation,
  TopicEducationBenefitsAndWorkStudy,
  TopicSpeciallyAdapatedHousing,
  TopicVeteranReadinessAndEmploymentChapter31,
  whoIsYourQuestionAboutLabels,
} from '../constants';

export const ServerErrorAlert = () => (
  <>
    <h2
      slot="headline"
      className="vads-u-font-size--h3 vads-u-margin-y--0 vads-u-font-size--lg"
    >
      We’re sorry. Something went wrong on our end
    </h2>
    <p className="vads-u-font-size--base">
      Refresh this page or check back later. You can also sign out of VA.gov and
      try signing back into this page.
    </p>
  </>
);

export const getContactMethods = (contactPreferences = []) => {
  let contactMethods = {};

  if (contactPreferences.length > 0) {
    contactPreferences.forEach(item => {
      if (item === 'Phone') contactMethods.Phone = 'Phone call';
      if (item === 'Email') contactMethods.Email = 'Email';
      if (item === 'USMail') contactMethods.USMail = 'U.S. mail';
    });
  } else {
    contactMethods = {
      Phone: 'Phone call',
      Email: 'Email',
      USMail: 'U.S. mail',
    };
  }

  return contactMethods;
};

export const isEqualToOnlyEmail = obj => {
  const keys = Object.keys(obj);
  return keys.length === 1 && keys[0] === 'Email' && obj.Email === 'Email';
};

// Reference Rules: https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields%2C%20options%20and%20labels/Location%20and%20postal%20code.md#guardianship-and-vre
export const isLocationOfResidenceRequired = data => {
  const {
    contactPreference,
    relationshipToVeteran,
    selectCategory,
    selectTopic,
    whoIsYourQuestionAbout,
    isQuestionAboutVeteranOrSomeoneElse,
  } = data;

  // Check if location is required based on contact preference
  if (contactPreference === 'USMail') {
    return false;
  }

  // Guardianship, VR&E , and Health rules
  const GuardianshipAndVRE =
    (selectCategory === CategoryGuardianshipCustodianshipFiduciaryIssues ||
      selectCategory === CategoryVeteranReadinessAndEmployment) &&
    selectTopic !== 'Other';

  const EducationAndVRE =
    selectCategory === CategoryEducation &&
    selectTopic === TopicVeteranReadinessAndEmploymentChapter31;

  // About myself
  // Flow 1.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN
  ) {
    return true;
  }

  // Flow 1.2
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER
  ) {
    return true;
  }

  // About someone else
  // Flow 2.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN
  ) {
    return true;
  }

  // Flow 2.2.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.VETERAN
  ) {
    return true;
  }

  // Flow 2.2.2
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.SOMEONE_ELSE
  ) {
    return true;
  }

  // Flow 2.3
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.WORK
  ) {
    return true;
  }

  // Flow 3.1
  // eslint-disable-next-line sonarjs/prefer-single-boolean-return
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.GENERAL
  ) {
    return true;
  }

  // Default to false if none of the conditions are met
  return false;
};

// Reference Rules: https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields%2C%20options%20and%20labels/Location%20and%20postal%20code.md#guardianship-and-vre
export const isPostalCodeRequired = data => {
  const {
    contactPreference,
    relationshipToVeteran,
    selectCategory,
    selectTopic,
    whoIsYourQuestionAbout,
    isQuestionAboutVeteranOrSomeoneElse,
    yourLocationOfResidence,
    familyMembersLocationOfResidence,
    veteransLocationOfResidence,
    yourHealthFacility,
  } = data;

  // Check if location is required based on contact preference
  if (contactPreference === 'USMail') {
    return false;
  }

  // Guardianship, VR&E , and Health rules
  const GuardianshipAndVRE =
    (selectCategory === CategoryGuardianshipCustodianshipFiduciaryIssues ||
      selectCategory === CategoryVeteranReadinessAndEmployment) &&
    selectTopic !== 'Other';

  const EducationAndVRE =
    selectCategory === CategoryEducation &&
    selectTopic === TopicVeteranReadinessAndEmploymentChapter31;

  const HealthCare = selectCategory === CategoryHealthCare;

  const HealthFacilityNotSelected = !yourHealthFacility;

  // About myself
  // Flow 1.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN &&
    statesRequiringPostalCode.includes(yourLocationOfResidence)
  ) {
    return true;
  }
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // Flow 1.2
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    statesRequiringPostalCode.includes(yourLocationOfResidence)
  ) {
    return true;
  }
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // About someone else
  // Flow 2.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN &&
    statesRequiringPostalCode.includes(familyMembersLocationOfResidence)
  ) {
    return true;
  }
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // Flow 2.2.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.VETERAN &&
    statesRequiringPostalCode.includes(veteransLocationOfResidence)
  ) {
    return true;
  }
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.VETERAN &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // Flow 2.2.2
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.SOMEONE_ELSE &&
    statesRequiringPostalCode.includes(familyMembersLocationOfResidence)
  ) {
    return true;
  }
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.SOMEONE_ELSE &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // Flow 2.3
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.WORK &&
    statesRequiringPostalCode.includes(veteransLocationOfResidence)
  ) {
    return true;
  }
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    relationshipToVeteran === relationshipOptionsSomeoneElse.WORK &&
    isQuestionAboutVeteranOrSomeoneElse ===
      isQuestionAboutVeteranOrSomeoneElseLabels.VETERAN &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // Flow 3.1
  if (
    (GuardianshipAndVRE || EducationAndVRE) &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.GENERAL &&
    statesRequiringPostalCode.includes(yourLocationOfResidence)
  ) {
    return true;
  }
  // eslint-disable-next-line sonarjs/prefer-single-boolean-return
  if (
    HealthCare &&
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.GENERAL &&
    HealthFacilityNotSelected
  ) {
    return true;
  }

  // Default to false if none of the conditions are met
  return false;
};

// Reference Rules: https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields,%20options%20and%20labels/Field%20rules.md#state-of-property
export const isStateOfPropertyRequired = data => {
  const { selectCategory, selectTopic } = data;

  return (
    selectCategory === CategoryHousingAssistanceAndHomeLoans &&
    (selectTopic === TopicSpeciallyAdapatedHousing ||
      selectTopic === TopicAppraisals)
  );
};

// List of categories required for Branch of service rule: https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields%2C%20options%20and%20labels/Field%20rules.md#branch-of-service
export const isBranchOfServiceRequired = data => {
  const { selectCategory, selectTopic, whoIsYourQuestionAbout } = data;

  return (
    (branchOfServiceRuleforCategories.includes(selectCategory) ||
      (selectCategory === CategoryBenefitsIssuesOutsidetheUS &&
        selectTopic === TopicDisabilityCompensation)) &&
    whoIsYourQuestionAbout !== whoIsYourQuestionAboutLabels.GENERAL
  );
};

// Veteran Readiness and Employment (VR&E) rules: https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields%2C%20options%20and%20labels/Field%20rules.md#veteran-readiness-and-employment-vre-information
export const isVRERequired = data => {
  const { selectCategory, selectTopic } = data;

  return (
    selectCategory === CategoryVeteranReadinessAndEmployment ||
    (selectCategory === CategoryEducation &&
      selectTopic === TopicVeteranReadinessAndEmploymentChapter31)
  );
};

export const isHealthFacilityRequired = data => {
  const { selectCategory, selectTopic } = data;

  const healthTopics = [
    'Prosthetics',
    'Audiology and hearing aids',
    'Getting care at a local VA medical center',
  ];

  return (
    (selectCategory === 'Health care' && healthTopics.includes(selectTopic)) ||
    (selectCategory ===
      'Debt for benefit overpayments and health care copay bills' &&
      selectTopic === 'Health care copay debt')
  );
};

// Based on Mural flow to make the YourVAHealthFacility component title dynamic (BE only expects yourHealthFacility for any option)
export const getHealthFacilityTitle = data => {
  const {
    YOUR_VA_HEALTH_FACILITY,
    VETERAN_VA_HEALTH_FACILITY,
    FAMILY_MEMBER_VA_HEALTH_FACILITY,
  } = CHAPTER_3;

  const {
    whoIsYourQuestionAbout,
    relationshipToVeteran,
    isQuestionAboutVeteranOrSomeoneElse,
  } = data;

  if (
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF ||
    whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.GENERAL
  ) {
    return YOUR_VA_HEALTH_FACILITY.TITLE;
  }

  if (whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.SOMEONE_ELSE) {
    if (relationshipToVeteran === relationshipOptionsSomeoneElse.VETERAN) {
      return FAMILY_MEMBER_VA_HEALTH_FACILITY.TITLE;
    }

    if (
      relationshipToVeteran === relationshipOptionsSomeoneElse.FAMILY_MEMBER
    ) {
      if (
        isQuestionAboutVeteranOrSomeoneElse ===
        isQuestionAboutVeteranOrSomeoneElseLabels.VETERAN
      ) {
        return VETERAN_VA_HEALTH_FACILITY.TITLE;
      }
      if (
        isQuestionAboutVeteranOrSomeoneElse ===
        isQuestionAboutVeteranOrSomeoneElseLabels.SOMEONE_ELSE
      ) {
        return FAMILY_MEMBER_VA_HEALTH_FACILITY.TITLE;
      }
    }

    if (relationshipToVeteran === relationshipOptionsSomeoneElse.WORK) {
      return VETERAN_VA_HEALTH_FACILITY.TITLE;
    }
  }

  return YOUR_VA_HEALTH_FACILITY.TITLE;
};

// Helper functions for statuses https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields%2C%20options%20and%20labels/Statuses%20and%20triggers.md
export const getVAStatusFromCRM = status => {
  switch (status.toLowerCase()) {
    case 'new':
    case 'in progress':
    case 'inprogress':
    case 'In progress':
      return 'In progress';
    case 'solved':
    case 'replied':
      return 'Replied';
    case 'reopened':
      return 'Reopened';
    case 'closed':
      return 'Closed';
    case 'question not found':
      return "We didn't find any questions with this reference number. Check your reference number and try again.";
    case 'questionnotfound':
      return "We didn't find any questions with this reference number. Check your reference number and try again.";
    default:
      return 'In progress';
  }
};

export const formatDate = (dateString, formatType = 'short') => {
  let parsedDate = parse(dateString, 'MM/dd/yyyy h:mm:ss a', new Date());

  if (!isValid(parsedDate)) {
    parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
  }

  if (!isValid(parsedDate)) {
    return dateString;
  }

  const dateFormat = formatType === 'long' ? 'MMMM d, yyyy' : 'MMM d, yyyy';

  return format(parsedDate, dateFormat);
};

// Helper for uploading multiple files
export const getFiles = files => {
  if (!files) {
    return [
      {
        FileName: null,
        FileContent: null,
      },
    ];
  }

  return files.map(file => {
    return {
      FileName: file.fileName,
      FileContent: file.base64,
    };
  });
};

export const getFileSizeMB = size => size * 0.00000095367432;

export const DownloadLink = ({ fileUrl, fileName, fileSize }) => {
  const fileSizeText = fileSize
    ? ` (${getFileSizeMB(fileSize).toFixed(2)} MB)`
    : '';

  return (
    <a href={fileUrl} download={fileName}>
      {`${fileName}${fileSizeText}`}
    </a>
  );
};

DownloadLink.propTypes = {
  fileName: PropTypes.string.isRequired,
  fileUrl: PropTypes.string.isRequired,
  fileSize: PropTypes.number,
};

export const isEducationNonVRE = formData =>
  formData.selectCategory === CategoryEducation &&
  formData.selectTopic !== TopicVeteranReadinessAndEmploymentChapter31;

export const isOutsideUSEducation = formData =>
  formData.selectCategory === CategoryBenefitsIssuesOutsidetheUS &&
  formData.selectTopic === TopicEducationBenefitsAndWorkStudy;

// Who is your question about? rules:
// CATEGORY = EDUCATION BENEFITS AND WORK STUDY
// AND
// TOPIC =/ VETERAN READINESS & EMPLOYMENT
//
// ALSO HIDDEN IF:
// CATEGORY =  BENEFITS ISSUES OUTSIDE THE US
// AND
// TOPIC = EDUCATION BENEFITS AND WORK STUDY
//
// BECAUSE 'EDU' QUESTIONS ARE SENT AS "GENERAL QUESTIONS" TO CRM. BUT SHOULD CONTINUE DOWN THE 'SOMEONE ELSE' FLOW.
export const whoIsYourQuestionAboutCondition = formData => {
  return !(isEducationNonVRE(formData) || isOutsideUSEducation(formData));
};

export const aboutMyselfRelationshipVeteranCondition = formData => {
  return (
    formData.whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    formData.relationshipToVeteran === relationshipOptionsMyself.VETERAN
  );
};

export const aboutMyselfRelationshipFamilyMemberCondition = formData => {
  return (
    formData.whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.MYSELF &&
    formData.relationshipToVeteran === relationshipOptionsMyself.FAMILY_MEMBER
  );
};

export const aboutSomeoneElseRelationshipVeteranCondition = formData => {
  return (
    formData.whoIsYourQuestionAbout ===
      whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    formData.relationshipToVeteran === relationshipOptionsMyself.VETERAN &&
    // EDU doesn't apply except when EDU + VRE
    (formData.selectCategory !== CategoryEducation ||
      (formData.selectCategory === CategoryEducation &&
        formData.selectTopic === TopicVeteranReadinessAndEmploymentChapter31))
  );
};

export const aboutSomeoneElseRelationshipFamilyMemberCondition = formData => {
  return (
    formData.whoIsYourQuestionAbout ===
      whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
    formData.relationshipToVeteran ===
      relationshipOptionsMyself.FAMILY_MEMBER &&
    // EDU doesn't apply except when EDU + VRE
    (formData.selectCategory !== CategoryEducation ||
      (formData.selectCategory === CategoryEducation &&
        formData.selectTopic === TopicVeteranReadinessAndEmploymentChapter31))
  );
};

export const aboutSomeoneElseRelationshipFamilyMemberAboutVeteranCondition =
  formData => {
    return (
      formData.whoIsYourQuestionAbout ===
        whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
      formData.relationshipToVeteran ===
        relationshipOptionsMyself.FAMILY_MEMBER &&
      formData.isQuestionAboutVeteranOrSomeoneElse ===
        isQuestionAboutVeteranOrSomeoneElseLabels.VETERAN
    );
  };

export const aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMemberCondition =
  formData => {
    return (
      formData.whoIsYourQuestionAbout ===
        whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
      formData.relationshipToVeteran ===
        relationshipOptionsMyself.FAMILY_MEMBER &&
      formData.isQuestionAboutVeteranOrSomeoneElse ===
        isQuestionAboutVeteranOrSomeoneElseLabels.SOMEONE_ELSE
    );
  };

export const aboutSomeoneElseRelationshipConnectedThroughWorkCondition =
  formData => {
    return (
      formData.whoIsYourQuestionAbout ===
        whoIsYourQuestionAboutLabels.SOMEONE_ELSE &&
      formData.relationshipToVeteran === relationshipOptionsSomeoneElse.WORK &&
      // EDU doesn't apply except when EDU + VRE
      (formData.selectCategory !== CategoryEducation ||
        (formData.selectCategory === CategoryEducation &&
          formData.selectTopic === TopicVeteranReadinessAndEmploymentChapter31))
    );
  };

export const aboutSomeoneElseRelationshipConnectedThroughWorkEducationCondition =
  formData => {
    return (
      formData.relationshipToVeteran === relationshipOptionsSomeoneElse.WORK &&
      (isEducationNonVRE(formData) || isOutsideUSEducation(formData))
    );
  };

export const aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducationCondition =
  formData => {
    return (
      formData.relationshipToVeteran !== relationshipOptionsSomeoneElse.WORK &&
      (isEducationNonVRE(formData) || isOutsideUSEducation(formData))
    );
  };

export const generalQuestionCondition = formData => {
  return (
    formData.whoIsYourQuestionAbout === whoIsYourQuestionAboutLabels.GENERAL
  );
};
